import { BLOCKS, CLOSE, FEEL, SMELLS, VERDICTS, cap } from './memory-map-data';

const AGE_QUESTION_ID = 5901;
const GENDER_QUESTION_ID = 5902;
const CLOSE_QUESTION_IDS = [5951, 5952];
const NAME_QUESTION_ID = 5960;
const CHAPTER_PICK_ID = 5970;
const FIRST_QUESTION_ID = 5910;
const QUESTIONS_PER_BLOCK_STEP = 10;

const POINTS = { n: 0, w: 1, b: 2, u: 2, h: 3 };
const SIGNS = ['stone', 'shape', 'glow'];
const SIGN_VALUES = { stone: [12, 52, 96], shape: [6, 52, 96], glow: [8, 52, 96] };
const DEFAULT_SIGN_VALUE = 52;
const DEFAULT_CLARITY_VALUE = 8;
const SIGN_LEVEL_BORDERS = [34, 67];

function getBlockQuestionId(blockIndex, questionIndex) {
    return FIRST_QUESTION_ID + blockIndex * QUESTIONS_PER_BLOCK_STEP + questionIndex;
}

function getSignLevel(value) {
    if (value < SIGN_LEVEL_BORDERS[0]) {
        return 1;
    }

    return value < SIGN_LEVEL_BORDERS[1] ? 2 : 3;
}

function findResponse(userResponses, questionId) {
    return userResponses.find(userResponse => userResponse.questionId === questionId);
}

/* Rebuilds the prototype's answers map (`b{block}q{question}`, `argue`, `untold`) from the stored user responses */
function getAnswers(userResponses = []) {
    const answers = {};

    BLOCKS.forEach((block, blockIndex) => block.q.forEach((question, questionIndex) => {
        const response = findResponse(userResponses, getBlockQuestionId(blockIndex, questionIndex));
        const key = `b${blockIndex}q${questionIndex}`;

        if (!response) {
            return;
        }

        if (question.t === 'smells') {
            answers[key] = { good: response.good || [], bad: response.bad || [] };
        } else if (SIGNS.includes(question.t)) {
            answers[key] = response.level;
        } else if (question.t === 'clarity') {
            answers[key] = response.value;
        } else if (typeof response.answerIndex === 'number') {
            answers[key] = response.answerIndex;
        }
    }));

    CLOSE.forEach((close, closeIndex) => {
        const response = findResponse(userResponses, CLOSE_QUESTION_IDS[closeIndex]);

        if (response && typeof response.answerIndex === 'number') {
            answers[close.id] = response.answerIndex;
        }
    });

    return answers;
}

const isAnswered = value => value !== undefined && value !== null;

function getLevel(avg) {
    if (avg >= 2.5) {
        return 'heavy';
    }

    return avg >= 1.8 ? 'mixed' : 'light';
}

function getUnsaid({ argue, untold }) {
    if (isAnswered(argue) && CLOSE[0].o[argue][2]) {
        return `The conversation you still have in your head, with ${CLOSE[0].o[argue][2]}.`;
    }

    if (isAnswered(untold) && untold < 2) {
        return 'A story you’ve never told anyone.';
    }

    return 'Something you haven’t found the words for yet.';
}

function getMemoryMapScore(answers) {
    const chapters = BLOCKS.map(() => ({ pts: [], count: 0, carry: 0, heavy: null, warm: null, state: 2 }));

    BLOCKS.forEach((block, blockIndex) => block.q.forEach((question, questionIndex) => {
        const answer = answers[`b${blockIndex}q${questionIndex}`];

        if (answer === undefined || answer === null || question.t === 'clarity') {
            return;
        }

        const chapter = chapters[blockIndex];
        const add = (points, phrase) => {
            if (points > 0) {
                chapter.pts.push(points);
                chapter.count++;
            }

            if (points === 3) {
                chapter.carry++;

                if (!chapter.heavy && phrase) {
                    chapter.heavy = phrase;
                }
            }

            if (points === 1 && !chapter.warm && phrase) {
                chapter.warm = phrase;
            }
        };

        if (question.t === 'feel') {
            add(POINTS[FEEL[answer][1]], FEEL[answer][2]);
        } else if (question.t === 'grid') {
            add(POINTS[question.o[answer][2]], question.o[answer][3]);
        } else if (question.t === 'smells') {
            const good = answer.good || [];
            const bad = answer.bad || [];

            chapter.count += good.length + bad.length;

            if (good.length && !chapter.warm) {
                chapter.warm = SMELLS.good[good[0]][2];
            }

            if (bad.length) {
                chapter.pts.push(3);
                chapter.carry++;

                if (!chapter.heavy) {
                    chapter.heavy = SMELLS.bad[bad[0]][2];
                }
            }
        } else if (SIGNS.includes(question.t)) {
            chapter.state = answer;
            chapter.pts.push(answer);

            if (answer === 3) {
                chapter.carry++;

                if (!chapter.heavy) {
                    chapter.heavy = question.hook;
                }
            }
        } else {
            add(POINTS[question.o[answer][1]], question.o[answer][2]);
        }
    }));

    const scored = chapters.map(chapter => {
        const avg = chapter.pts.length ? chapter.pts.reduce((sum, points) => sum + points, 0) / chapter.pts.length : 1;

        return { ...chapter, avg, level: getLevel(avg) };
    });

    let top = 0;

    scored.forEach((chapter, index) => {
        if (chapter.avg > scored[top].avg) {
            top = index;
        }
    });

    const total = scored.reduce((sum, chapter) => sum + chapter.count, 0);
    const carry = scored.reduce((sum, chapter) => sum + chapter.carry, 0);
    const anyHeavy = scored[top].heavy || scored.map(chapter => chapter.heavy).find(Boolean) || 'the things you never said';
    const anyWarm = scored.map(chapter => chapter.warm).find(Boolean) || 'the good days';

    return { ch: scored, top, total, carry, unsaid: getUnsaid(answers), anyHeavy, anyWarm };
}

function getMemoryMap(userResponses) {
    return getMemoryMapScore(getAnswers(userResponses));
}

function getMapLine(blockIndex, chapter) {
    const verdict = VERDICTS[BLOCKS[blockIndex].key][chapter.level];
    const phrase = chapter.level === 'light'
        ? (chapter.warm || 'a few good days')
        : (chapter.heavy || chapter.warm || 'a few quiet moments');

    return [verdict[0], verdict[1](phrase)];
}

function getChapterOrder(map) {
    return [0, 1, 2].sort((a, b) => (map.ch[b].avg - map.ch[a].avg) || (a - b));
}

function getUserName(userResponses = []) {
    return findResponse(userResponses, NAME_QUESTION_ID)?.text || '';
}

function getChosenChapter(userResponses, map) {
    const chosen = findResponse(userResponses || [], CHAPTER_PICK_ID)?.chapterIndex;

    return typeof chosen === 'number' ? chosen : map.top;
}

export {
    AGE_QUESTION_ID,
    GENDER_QUESTION_ID,
    CLOSE_QUESTION_IDS,
    NAME_QUESTION_ID,
    CHAPTER_PICK_ID,
    SIGNS,
    SIGN_VALUES,
    DEFAULT_SIGN_VALUE,
    DEFAULT_CLARITY_VALUE,
    cap,
    getBlockQuestionId,
    getSignLevel,
    getAnswers,
    getMemoryMapScore,
    getMemoryMap,
    getMapLine,
    getChapterOrder,
    getUserName,
    getChosenChapter
};
